import { useEffect, useState } from 'react';
import { t } from '@/i18n';
import type {
  RegistrationResponse,
  RegistrationMode,
  AuthUserContext,
  UserRegistration,
  PublicEventQuestion,
} from '../types';
import {
  forgetRegistration,
  getRememberedRegistration,
  rememberRegistration,
  type RememberedRegistration,
} from '../registrationMemory';
import RegistrationForm from './RegistrationForm';
import RegistrationConfirmation from './RegistrationConfirmation';
import RegisteredCard from './RegisteredCard';

// The desktop and mobile CtaButton both mount, so memoize the status lookup by
// token to fire a single request. Resolves to null on 404/network error, which
// the caller treats as "no longer registered" and forgets the token.
const statusRequests = new Map<string, Promise<RegistrationResponse | null>>();

function fetchRegistrationStatus(token: string): Promise<RegistrationResponse | null> {
  let request = statusRequests.get(token);
  if (!request) {
    request = (async (): Promise<RegistrationResponse | null> => {
      try {
        const res = await fetch(`/api/registrations/${token}/status/`);
        return res.ok ? ((await res.json()) as RegistrationResponse) : null;
      } catch {
        return null;
      }
    })();
    statusRequests.set(token, request);
  }
  return request;
}

interface CtaButtonProps {
  eventId: number;
  eventSlug: string;
  isFull: boolean;
  hasStarted: boolean;
  registrationMode: RegistrationMode;
  variant: 'desktop' | 'mobile';
  authUser: AuthUserContext | null;
  userRegistration: UserRegistration | null;
  questions?: PublicEventQuestion[];
  spotsRemaining?: number | null;
  price?: string | null;
  currency?: string;
}

/** Layer A — the unconditional "Already registered? Find my registration" link. */
function AlreadyRegisteredLink() {
  return (
    <p className="mt-3 text-center text-xs text-gray-400 dark:text-gray-500">
      {t('Already registered?')}{' '}
      <a href="/check-status/" className="font-medium text-teal-600 hover:text-teal-500 dark:text-teal-400">
        {t('Find my registration')}
      </a>
    </p>
  );
}

export default function CtaButton({
  eventId,
  eventSlug,
  isFull,
  hasStarted,
  registrationMode,
  variant,
  authUser,
  userRegistration,
  questions = [],
  spotsRemaining,
  price,
  currency,
}: CtaButtonProps) {
  const [showForm, setShowForm] = useState(false);
  const [registration, setRegistration] = useState<RegistrationResponse | null>(null);

  // Layer B — same-device memory. Read the remembered token, then fetch fresh
  // status so a cancelled/promoted registration is never shown from a snapshot.
  // Layer C (logged-in, server-rendered) always wins, so skip the fetch then.
  const [remembered, setRemembered] = useState<RememberedRegistration | null>(() =>
    userRegistration ? null : getRememberedRegistration(eventId)
  );
  const [rememberedStatus, setRememberedStatus] = useState<RegistrationResponse | null>(null);

  useEffect(() => {
    if (!remembered) return;
    let cancelled = false;
    fetchRegistrationStatus(remembered.token).then((data) => {
      if (cancelled) return;
      // Forget (and degrade to the form) when the registration is gone (404/error)
      // or in a terminal-negative state — "you're registered" is no longer true, so
      // it must not render in the registered card.
      if (!data || data.status === 'cancelled') {
        forgetRegistration(eventId);
        setRemembered(null);
      } else {
        setRememberedStatus(data);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [eventId, remembered]);

  function handleRegistered(reg: RegistrationResponse) {
    setRegistration(reg);
    // Remember this device so a return visit lands on the registered state (Layer B).
    rememberRegistration(eventId, { token: reg.manage_token, email: reg.email });
  }

  function handleNotYou() {
    // Shared-device escape: forget the remembered registration and open the form.
    forgetRegistration(eventId);
    setRemembered(null);
    setRememberedStatus(null);
    setShowForm(true);
  }

  // Registered state shown in place of the form (Layer C wins over Layer B),
  // but only until the visitor signs up in-session or escapes via "Not you?".
  const registeredCard =
    !registration && userRegistration ? (
      <RegisteredCard
        status={userRegistration.status}
        paymentStatus={userRegistration.payment_status}
        waitlistPosition={userRegistration.waitlist_position}
        price={price}
        currency={currency}
        tone="blue"
        primaryHref="/app/joined-events"
        primaryLabel={t('View in My Signups')}
      />
    ) : !registration && !showForm && rememberedStatus ? (
      <RegisteredCard
        status={rememberedStatus.status}
        paymentStatus={rememberedStatus.payment_status}
        waitlistPosition={rememberedStatus.waitlist_position}
        price={price}
        currency={currency}
        subline={rememberedStatus.email}
        tone="teal"
        primaryHref={`/event-signup/${rememberedStatus.manage_token}/`}
        primaryLabel={t('View my registration')}
        onNotYou={handleNotYou}
      />
    ) : null;

  const formEl = (
    <RegistrationForm
      eventSlug={eventSlug}
      registrationMode={registrationMode}
      isFull={isFull}
      onRegistered={handleRegistered}
      authUser={authUser}
      questions={questions}
      spotsRemaining={spotsRemaining}
      price={price}
      currency={currency}
    />
  );

  // Mobile variant: sticky bottom bar (registered state renders inline at page end).
  if (variant === 'mobile') {
    if (registration) {
      return null; // Hide mobile CTA after an in-session signup (confirmation is on desktop card).
    }

    if (registeredCard) {
      return registeredCard;
    }

    if (hasStarted) {
      return (
        <div className="fixed inset-x-0 bottom-0 z-40 border-t border-gray-200 bg-white/90 px-4 py-3 backdrop-blur-lg dark:border-gray-800 dark:bg-gray-950/90">
          <p className="text-center text-sm font-semibold text-gray-900 dark:text-white">{t('Registration closed')}</p>
          <p className="text-center text-sm text-gray-500 dark:text-gray-400">{t('This event has already started.')}</p>
          <AlreadyRegisteredLink />
        </div>
      );
    }

    if (showForm) {
      return (
        <div className="fixed inset-x-0 bottom-0 z-40 max-h-[80vh] overflow-y-auto border-t border-gray-200 bg-white px-4 py-4 shadow-2xl dark:border-gray-800 dark:bg-gray-950">
          <div className="mb-3 flex items-center justify-between">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white">{t('Register')}</h3>
            <button
              type="button"
              onClick={() => setShowForm(false)}
              className="text-sm text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            >
              {t('Close')}
            </button>
          </div>
          {formEl}
          <AlreadyRegisteredLink />
        </div>
      );
    }

    return (
      <div className="fixed inset-x-0 bottom-0 z-40 border-t border-gray-200 bg-white/90 px-4 py-3 backdrop-blur-lg dark:border-gray-800 dark:bg-gray-950/90">
        <button
          type="button"
          onClick={() => setShowForm(true)}
          className="w-full rounded-xl bg-teal-600 px-6 py-3.5 text-base font-semibold text-white shadow-lg shadow-teal-500/30 transition-colors hover:bg-teal-500 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal-500"
        >
          {isFull ? t('Join Waitlist') : t('Register')}
        </button>
        <AlreadyRegisteredLink />
      </div>
    );
  }

  // Desktop variant: card in sidebar
  if (registration) {
    return (
      <div className="rounded-2xl border border-gray-200 border-t-2 border-t-teal-500 bg-white p-6 shadow-sm dark:border-gray-800 dark:border-t-teal-500 dark:bg-gray-950">
        <RegistrationConfirmation registration={registration} price={price} currency={currency} />
      </div>
    );
  }

  if (registeredCard) {
    return registeredCard;
  }

  if (hasStarted) {
    return (
      <div className="rounded-2xl border border-gray-200 bg-white p-6 text-center shadow-sm dark:border-gray-800 dark:bg-gray-950">
        <p className="text-sm font-semibold text-gray-900 dark:text-white">{t('Registration closed')}</p>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{t('This event has already started.')}</p>
        <AlreadyRegisteredLink />
      </div>
    );
  }

  if (showForm) {
    return (
      <div className="rounded-2xl border border-gray-200 border-t-2 border-t-teal-500 bg-white p-6 shadow-sm dark:border-gray-800 dark:border-t-teal-500 dark:bg-gray-950">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white">{t('Register')}</h3>
          <button
            type="button"
            onClick={() => setShowForm(false)}
            className="text-xs text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            {t('Back')}
          </button>
        </div>
        {formEl}
        <AlreadyRegisteredLink />
      </div>
    );
  }

  return (
    <div>
      <button
        type="button"
        onClick={() => setShowForm(true)}
        className="w-full rounded-lg bg-teal-600 px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-teal-500 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal-500"
      >
        {isFull ? t('Join Waitlist') : t('Register')}
      </button>
      <AlreadyRegisteredLink />
    </div>
  );
}
